import { firstSentences, pageLabel } from '../armKit';
import { deepWorkFacts } from '../pageData';
import { cssRule, EDGE, suppressAbstractCss } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;

const FLAG = 'cliffhanger';
const TEASER_CLASS = 'profile-liautotest-cliffhanger';
const HANDOFF_CLASS = `${TEASER_CLASS}__handoff`;
const CONTINUE_CLASS = `${TEASER_CLASS}__continue`;
const SENTENCE_COUNT = 2;
const CONTINUE_LABEL = 'Continue reading';
const CONTINUE_SOURCE = 'profile-liautotest-cliffhanger-continue';

// The teaser and the handoff row land in the same card item the page's
// abstract spans sit in, so both inherit the abstract's type scale and
// colour; their <p> margins are flattened so the pill's own margin-top is
// the one gap between them. The pill keeps the link colour it inherits and
// borrows EDGE for its outline, with hover pinned so the page's link
// underline cannot creep into a button. The suppress recipe rides the
// per-card flag under the arm scope, so a card the arm left unflagged keeps
// every span it was served.
function armCss(scope: string, treated: string): string {
  return (
    suppressAbstractCss(`${scope} ${treated}`) +
    cssRule(
      [`${scope} .${TEASER_CLASS}`, `${scope} .${HANDOFF_CLASS}`],
      'margin: 0;',
    ) +
    cssRule(
      [`${scope} .${CONTINUE_CLASS}`],
      'display: inline-block; margin-top: 8px; padding: 6px 16px; ' +
        `border: 1px solid ${EDGE}; border-radius: 16px; ` +
        'font-size: 13px; font-weight: 600; white-space: nowrap;',
    ) +
    cssRule([`${scope} .${CONTINUE_CLASS}:hover`], 'text-decoration: none;')
  );
}

function marked(card: ProfileCard): boolean {
  return card.flag(FLAG) != null;
}

function teaserFor(abstract: string): HTMLParagraphElement {
  const teaser = document.createElement('p');
  teaser.className = TEASER_CLASS;
  teaser.textContent = firstSentences(abstract, SENTENCE_COUNT);

  return teaser;
}

function continueLabel(pages: string | null): string {
  return pages == null ? CONTINUE_LABEL : `${CONTINUE_LABEL} (${pages})`;
}

function handoffFor(
  card: ProfileCard,
  url: string,
  facts: Facts,
  ctx: ArmContext,
): HTMLParagraphElement {
  const row = document.createElement('p');
  row.className = HANDOFF_CLASS;

  const handoff = document.createElement('a');
  handoff.className = CONTINUE_CLASS;
  handoff.href = url;
  handoff.textContent = continueLabel(
    pageLabel(facts.get(card.workId)?.pageCount),
  );
  handoff.addEventListener('click', () => {
    ctx.track(CONTINUE_SOURCE, { entityId: card.workId });
  });
  row.appendChild(handoff);

  return row;
}

function treat(card: ProfileCard, facts: Facts, ctx: ArmContext): boolean {
  if (marked(card)) return false;

  // A card with an abstract but no link gets no handoff, so it is left whole:
  // suppressing its spans would delete its only abstract.
  const abstract = card.abstractText();
  if (abstract === '' || card.url == null) return false;

  // Two appends into the same card item: the teaser paragraph, then the
  // button row beneath it. Once the first lands the item is known to exist,
  // so the second cannot miss.
  if (!card.appendAfterAbstract(teaserFor(abstract))) return false;
  card.appendAfterAbstract(handoffFor(card, card.url, facts, ctx));

  // Flagged only once the replacement is in the card: the flag is what arms
  // the suppress rule, and arming it over nothing would empty the card.
  ctx.injectStyles(armCss(ctx.scope, card.setFlag(FLAG)));

  return true;
}

// `first` comes off the cards' own flags rather than module state, so
// whichever pass treats the first card on the page — the initial render, or a
// scroll batch when nothing served qualified — is the one that marks exposure.
function treatAll(cards: ProfileCard[], facts: Facts, ctx: ArmContext): void {
  const first = !ctx.cards().some(marked);
  const treated = cards.filter((card) => treat(card, facts, ctx));

  if (first && treated.length > 0) ctx.markRendered();
}

export const cliffhangerAbstract: ArmSpec = {
  // stamp stays unset: the treated set (any card with an abstract and a link)
  // is knowable synchronously, but the render itself waits on deepWorkFacts
  // for the handoff's page label, so the truthful exposure signal is
  // markRendered at the moment the first card is actually treated.
  onReady: async (ctx) => {
    const facts = await deepWorkFacts(ctx.viewedUserId);
    treatAll(ctx.cards(), facts, ctx);
  },

  // The harness feeds the server-rendered cards through here too; the per-card
  // flag is what makes that second pass — and any replayed batch — a no-op.
  onCards: async (cards, ctx) => {
    const facts = await deepWorkFacts(ctx.viewedUserId);
    treatAll(cards, facts, ctx);
  },
};
